'use client';

import { ResearchBlogEntry } from "@/app/lib/generated/graphql-types";
import {
    ResearchOverviewHeaderFrame,
    ResearchOverviewIntroFrame,
    ResearchOverviewTileCardFrame,
    ResearchOverviewTileImageFrame,
    ResearchOverviewTilesFrame,
} from "@/app/features/sites/research/ui/shared/ResearchOverviewFrames";
import SlideshowIntroV2 from "@/app/__to_be_integrated/_____components/_NEW/intros/slideshow/SlideshowIntroV2";
import { Box, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Image from "next/image";
import clsx from "clsx";
import styles from "@/app/features/sites/guided-tours/ui/public/guided-tours-public.module.css";
import { mediaRefAlt, mediaRefUrl } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import { useSiteResearchEditor } from "@/app/features/sites/research/contexts/SiteResearchEditorContext";
import ResearchOverviewItemEditorProvider, {
    useResearchOverviewItemEditor,
} from "@/app/features/sites/research/contexts/ResearchOverviewItemEditorContext";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import EditableMarkdownV2 from "@/app/features/shared/editable-markdown/EditableMarkdownV2";
import EditMediaButtonOverlay from "@/app/features/shared/EditMediaButtonOverlay";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";

const EXCERPT_EDITOR_HEIGHT = { xs: 260, md: 250 };

export default function ResearchOverviewEditable({ entries }: { entries: ResearchBlogEntry[] }) {
    const { editable, updateEditable, errorForField } = useSiteResearchEditor();

    if (!editable) return <LoadingSpinner />;

    const titleError = errorForField?.('intro.title') || undefined;
    const subtitleError = errorForField?.('intro.subtitle') || undefined;

    return (
        <>
            <ResearchOverviewIntroFrame>
                <SlideshowIntroV2
                    title={editable.intro.title}
                    subtitle={editable.intro.subtitle}
                    images={[editable.intro.image]}
                    heightPreset="reduced"
                    linkContentToAnchor={false}
                    startChildren={(
                        <EditMediaButtonOverlay
                            layout="inline"
                            onImageChange={(image) => {
                                if (image) updateEditable({ intro: { image } });
                            }}
                        />
                    )}
                    content={(
                        <Stack spacing={1} alignItems="center">
                            <EditableTypographyV2
                                label="Titel"
                                component="h1"
                                variant="h1"
                                value={editable.intro.title}
                                onChange={(title) => updateEditable({ intro: { title } })}
                                error={Boolean(titleError)}
                                helperText={titleError}
                            />
                            <EditableTypographyV2
                                label="Untertitel"
                                component="h2"
                                variant="subtitle2"
                                value={editable.intro.subtitle ?? ''}
                                onChange={(subtitle) => updateEditable({ intro: { subtitle } })}
                                error={Boolean(subtitleError)}
                                helperText={subtitleError}
                            />
                        </Stack>
                    )}
                />
            </ResearchOverviewIntroFrame>

            <ResearchOverviewHeaderFrame>
                <Typography component="h2" variant="h1" className="text-wrap md:text-nowrap">
                    Forschungsbeiträge
                </Typography>
            </ResearchOverviewHeaderFrame>

            <ResearchOverviewTilesFrame>
                {entries.map((entry) => (
                    <Grid key={entry.documentId} size={1}>
                        <ResearchOverviewItemEditorProvider documentId={entry.documentId}>
                            <ResearchOverviewTileEditable />
                        </ResearchOverviewItemEditorProvider>
                    </Grid>
                ))}
            </ResearchOverviewTilesFrame>
        </>
    );
}

function ResearchOverviewTileEditable() {
    const { editable, updateEditable, errorForField } = useResearchOverviewItemEditor();

    if (!editable) return null;

    const titleError = errorForField?.('title') || undefined;
    const subtitleError = errorForField?.('subtitle') || undefined;
    const excerptError = errorForField?.('excerpt') || undefined;
    const image = editable.image ?? null;
    const imageSrc = image ? mediaRefUrl(image) : PLACEHOLDER_IMAGE;
    const imageAlt = image ? (mediaRefAlt(image) ?? editable.title) : editable.title;

    return (
        <ResearchOverviewTileCardFrame animated={false}>
            <Stack spacing={2} className="flex flex-auto">
                <ResearchOverviewTileImageFrame>
                    <Image
                        src={imageSrc}
                        alt={imageAlt}
                        fill
                        loading="lazy"
                        quality={75}
                        sizes="(max-width: calc(var(--content-max-width) / 3)) 100vw, 33vw"
                        className={clsx('signature-border-radius', styles.tileImage)}
                    />
                    <EditMediaButtonOverlay onImageChange={(image) => updateEditable({ image })} />
                </ResearchOverviewTileImageFrame>

                <Stack spacing={2} className="md:px-4 pb-2">
                    <EditableTypographyV2
                        label="Titel"
                        component="h2"
                        variant="h2"
                        color="primary"
                        value={editable.title}
                        onChange={(title) => updateEditable({ title })}
                        error={Boolean(titleError)}
                        helperText={titleError}
                    />
                    <EditableTypographyV2
                        label="Untertitel"
                        component="p"
                        variant="subtitle1"
                        color="secondary"
                        value={editable.subtitle ?? ''}
                        onChange={(subtitle) => updateEditable({ subtitle })}
                        error={Boolean(subtitleError)}
                        helperText={subtitleError}
                    />

                    <Box
                        sx={{
                            display: "flex",
                            height: EXCERPT_EDITOR_HEIGHT,
                            minHeight: EXCERPT_EDITOR_HEIGHT,
                            maxHeight: EXCERPT_EDITOR_HEIGHT,
                            minWidth: 0,
                            overflow: "hidden",
                        }}
                    >
                        <EditableMarkdownV2
                            label="Kurzbeschreibung"
                            value={editable.excerpt ?? ''}
                            onChange={(excerpt) => updateEditable({ excerpt })}
                            typographyOverrides={{ paragraph: 'body2' }}
                            error={Boolean(excerptError)}
                            helperText={excerptError}
                            slots={{
                                root: {
                                    sx: {
                                        display: "flex",
                                        height: "100%",
                                        maxHeight: "100%",
                                        width: "100%",
                                        minHeight: 0,
                                        minWidth: 0,
                                        "& > [role='button']": {
                                            boxSizing: "border-box",
                                            display: "flex",
                                            flexDirection: "column",
                                            height: "100%",
                                            maxHeight: "100%",
                                            width: "100%",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                        },
                                    },
                                },
                                markdown: {
                                    root: {
                                        sx: {
                                            height: "100%",
                                            maxHeight: "100%",
                                            minHeight: 0,
                                            overflow: "hidden",
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                </Stack>
            </Stack>
        </ResearchOverviewTileCardFrame>
    );
}
